import { Component, inject, input, OnInit, output, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { PhysicalInventoryCountEnums } from 'src/app/enum/physical-inventory-count-enums';
import { PhysicalInventoryStateEnums } from 'src/app/enum/physical-inventory-state-enums';
import { ActionColor, CheckBoxAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { PhysicalInventoryDetailModel } from 'src/app/models/inventory/physical-inventory-detail.model';
import { PhysicalInventoryModel } from 'src/app/models/inventory/physical-inventory.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { PhysicalInventoryArticleResponseTable } from 'src/app/models/tables/inventory/physical-inventory-article-response-table.model';
import { PhysicalInventoryDetailRegisterTable } from 'src/app/models/tables/inventory/physical-inventory-detail-register-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { PhysicalInventoryDetailService } from 'src/app/services/inventory/physical-inventory-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { RegisterPhysicalInventoryDetailComponent } from '../register-physical-inventory-detail/register-physical-inventory-detail.component';

@Component({
  selector: 'app-physical-inventory-detail',
  templateUrl: './physical-inventory-detail.component.html',
  styleUrls: ['./physical-inventory-detail.component.scss'],
  standalone: false
})
export class PhysicalInventoryDetailComponent implements OnInit {

  @ViewChild('articleSelection') articleSelectionModalComponent?: CustomModalComponent | null = null;
  @ViewChild('countModal') countModalComponent?: CustomModalComponent | null = null;

  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly physicalInventoryDetailService = inject(PhysicalInventoryDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  disabledDetail = input<boolean>(false);
  permissionUpdatable = input<boolean>(true);
  physicalInventoryModel = input<PhysicalInventoryModel>(new PhysicalInventoryModel());
  quantityDetails = output<number>();

  ascendant: boolean = true;
  ascendantArticle: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    }
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  columnSortArticle: string | null = null;
  count: number = 0;
  countText: string = '';
  dataArticle: PagedResponseModel<PhysicalInventoryArticleResponseTable> = new PagedResponseModel<PhysicalInventoryArticleResponseTable>();
  detailData: PagedResponseModel<PhysicalInventoryDetailRegisterTable> = new PagedResponseModel<PhysicalInventoryDetailRegisterTable>();
  detailHeader: ColumnResponseModel[] = [];
  disabledButton: boolean = true;
  filters: ColumnResponseModel[] = [];
  filtersArticle: ColumnResponseModel[] = [];
  headerArticle: ColumnResponseModel[] = [];
  inputsForArticlesSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: false,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
    actions: []
  });
  inputsForCountModal$ = new BehaviorSubject<any>({
    physicalInventoryId: 0,
    permissionUpdatable: false,
    count: 0
  });
  limit: number = 10;
  limitArticle: number = 5;
  output$ = new Subject<any>();
  page: number = 1;
  pageArticle: number = 1;
  subscription: Subscription;
  visibleListDetail: boolean = true;
  componentTableSelection = CustomTableSelectComponent;
  registerCountModal = RegisterPhysicalInventoryDetailComponent;

  async ngOnInit(): Promise<void> {
    await this.getColumns();

    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeArticle(payload);
          break;
        case 'onFilterChange':
          this.onFilterChangeArticle(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  ngOnChanges() {
    if (this.physicalInventoryModel().physicalInventoryId !== 0 && this.physicalInventoryModel().physicalInventoryId !== -1) {
      this.filters = [];
      this.getPhysicalinventoryDetailsForRegisterTable();
      this.updateInputsForCountModal();
    } else {
      this.visibleListDetail = false;
    }

    const isNewOrClosed = this.physicalInventoryModel().physicalInventoryId === 0 ||
      this.physicalInventoryModel().physicalInventoryStateId !== PhysicalInventoryStateEnums.open;

    this.disabledButton = isNewOrClosed;
  }

  countReadyDetails(): number {
    return this.detailData.items.filter(detail => detail.ready === "Si").length;
  }

  getPercentage(): string {
    if (this.detailData.totalItems === 0) {
      return '0';
    }
    return ((this.countReadyDetails() / this.detailData.totalItems) * 100).toFixed(2);
  }


  async deleteSomeDetails(ids: number[]) {
    const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar el detalle seleccionado del inventario físico?');
    if (accepted) {
      try {
        await firstValueFrom(this.physicalInventoryDetailService.deleteSomeDetails(ids));
        this.toastHandlerService.showToast('success', 'El detalle del inventario físico fue eliminado exitosamente.', 'Atención');
        this.getPhysicalinventoryDetailsForRegisterTable();
        this.clearSelectionTrigger++;
      } catch (error) {
        console.error('Error al eliminar el detalle del inventario físico.', error);
      }
    }
  }

  async getColumns() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.physicalInventoryDetailService.getColumnsForRegisterTable());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getColumnsArticle() {
    try {
      this.headerArticle = await firstValueFrom(this.physicalInventoryDetailService.getColumnsForListArticleSelection());
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de artículos.', error);
    }
  }

  async getListArticleSelection() {
    try {
      this.dataArticle = await firstValueFrom(this.physicalInventoryDetailService.getListArticleSelection(
        this.physicalInventoryModel().physicalInventoryId, this.pageArticle, this.limitArticle,
        this.columnSortArticle, this.ascendantArticle, this.filtersArticle));
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los artículos', error);
    }
  }

  async getPhysicalinventoryDetailsForRegisterTable() {
    try {
      this.detailData = await firstValueFrom(this.physicalInventoryDetailService.getPhysicalinventoryDetailsForRegisterTable(
        this.page, this.limit, this.columnSort, this.ascendant, this.physicalInventoryModel().physicalInventoryId, this.filters)
      );
      this.quantityDetails.emit(this.detailData.totalItems);
      if (this.detailData.totalItems != 0) {
        this.visibleListDetail = true;
      } else {
        this.visibleListDetail = true;
      }
    } catch (error) {
      console.error('Error al buscar los detalles del inventario físico.', error);
    }
  }

  getUpdateListDetail(updateListDetail: boolean) {
    if (updateListDetail) {
      this.getPhysicalinventoryDetailsForRegisterTable();
    }
  }

  async handleSelectedItem(selectedItem: any) {
    if (selectedItem.length > 0) {
      const physicalInventoryDetails: PhysicalInventoryDetailModel[] = []
      selectedItem.forEach((element: {
        articleId: number;
        unitCost: number;
        inventoryQuantity: number;
        availableQuantity: number;
        workshopQuantity: number;
        storeQuantity: number;
        constructionSiteQuantity: number;
      }) => {
        const newPhysicalInventoryDetail = new PhysicalInventoryDetailModel();
        newPhysicalInventoryDetail.physicalInventoryId = this.physicalInventoryModel().physicalInventoryId;
        newPhysicalInventoryDetail.articleId = element.articleId;
        newPhysicalInventoryDetail.initialUnitCost = element.unitCost;
        newPhysicalInventoryDetail.constructionSiteQuantity = element.constructionSiteQuantity;
        newPhysicalInventoryDetail.workshopQuantity = element.workshopQuantity;
        newPhysicalInventoryDetail.availableQuantity = element.availableQuantity;
        newPhysicalInventoryDetail.storeQuantity = element.storeQuantity;

        physicalInventoryDetails.push(newPhysicalInventoryDetail);
      });

      await firstValueFrom(this.physicalInventoryDetailService.createSomeDetails(physicalInventoryDetails));
      this.toastHandlerService.showToast('success', 'El detalle del inventario físico fue creado exitosamente', '');
      this.getUpdateListDetail(true);
    }
  }

  async onAction(event: { action: string, item?: PhysicalInventoryDetailRegisterTable, selectedItems?: number[] }) {
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteSomeDetails(event.selectedItems);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
    this.updateInputs();
    this.getPhysicalinventoryDetailsForRegisterTable();
  }

  async onFilterChangeArticle(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersArticle = this.removeDuplicates(filters);
    this.filtersArticle.forEach((filter: any) => this.processFilterArticle(filter));
    this.updateInputs();
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getPhysicalinventoryDetailsForRegisterTable();
  }

  async onPageChangeArticle(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageArticle = event.page;
    this.limitArticle = event.pageSize;
    this.columnSortArticle = event.sortField || null;
    this.ascendantArticle = event.sortOrder === 1;
    this.getListArticleSelection();
  }

  async openArticleSelectionModal() {
    this.filtersArticle = [];
    await this.getColumnsArticle();
    await this.getListArticleSelection();
    this.updateInputs();
    this.articleSelectionModalComponent && this.articleSelectionModalComponent.show();
  }

  async openCountModal(countEnum: PhysicalInventoryCountEnums) {
    this.count = countEnum;
    this.countText = `Conteo ${this.count}`;
    this.filtersArticle = [];
    await this.getColumnsArticle();
    await this.getListArticleSelection();
    this.updateInputsForCountModal();
    this.countModalComponent && this.countModalComponent.show();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter == null || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  processFilterArticle(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter == null || valueFilter === '' || valueFilter === 0) {
      this.filtersArticle = this.filtersArticle.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersArticle.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  async reloadDetails(params: any) {
    if (params.reload) {
      await this.getPhysicalinventoryDetailsForRegisterTable();
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  updateInputs() {
    this.inputsForArticlesSelection$.next({
      headers: this.headerArticle,
      data: this.dataArticle,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      limitPaginator: true,
      filters: this.filtersArticle,
      actions: [
        {
          label: 'Imágenes',
          icon: 'pi pi-image',
          action: 'view-image'
        }
      ]
    });
  }

  updateInputsForCountModal() {
    this.inputsForCountModal$.next({
      physicalInventoryId: this.physicalInventoryModel().physicalInventoryId,
      permissionUpdatable: this.permissionUpdatable(),
      count: this.count
    });
  }

}
